/**
 * The favorite-team rules for Scores. Every function here reorders or filters
 * events the server already returned, and never fetches. A favorite can move a
 * game up a list or gather it into a section, but it can never add a game the
 * day read or the feed did not carry, which is how the date scope stays fixed.
 */
import type { typeScoresEventInput } from "./scoreRowModel";

/** True when either side of the event is a team the fan has favorited. */
export function isFavoriteEvent(input: {
  event: typeScoresEventInput;
  favoriteTeamIds: readonly string[];
}): boolean {
  const favorites = new Set(input.favoriteTeamIds);
  return (
    favorites.has(input.event.home.teamId) ||
    favorites.has(input.event.away.teamId)
  );
}

/** Partitions one already-fetched list into favorites then the rest. The
 *  server's own order is preserved inside each group, and the returned array
 *  always holds exactly the events that were passed in. */
export function sortFavoritesFirst(input: {
  events: readonly typeScoresEventInput[];
  favoriteTeamIds: readonly string[];
}): typeScoresEventInput[] {
  const favorites = new Set(input.favoriteTeamIds);
  const favorited: typeScoresEventInput[] = [];
  const rest: typeScoresEventInput[] = [];

  for (const event of input.events) {
    if (favorites.has(event.home.teamId) || favorites.has(event.away.teamId)) {
      favorited.push(event);
    } else {
      rest.push(event);
    }
  }

  return [...favorited, ...rest];
}

/** Gathers the fan's favorite teams' games out of the feed sections the All
 *  page already holds, in feed order, once each. It can only ever return
 *  events the caller passed in, so the favorites section is scoped to the same
 *  window the feed response was. Games with alerts on lead the card, then feed
 *  order (Scores UX spec v1.0 3.1, ISSUE-1197). */
export function collectFavoriteEvents(input: {
  sections: readonly {
    leagueId: string;
    events: readonly typeScoresEventInput[];
  }[];
  favoriteTeamIds: readonly string[];
}): typeScoresEventInput[] {
  const collected: typeScoresEventInput[] = [];
  const seenEventIds = new Set<string>();

  for (const section of input.sections) {
    for (const event of section.events) {
      if (seenEventIds.has(event.eventId)) {
        continue;
      }
      if (!isFavoriteEvent({ event, favoriteTeamIds: input.favoriteTeamIds })) {
        continue;
      }
      seenEventIds.add(event.eventId);
      collected.push(event);
    }
  }

  return [
    ...collected.filter((event) => event.hasAlertsOn === true),
    ...collected.filter((event) => event.hasAlertsOn !== true),
  ];
}
